import { useEffect, useMemo, useState } from "react";

import { ClipboardCopy, EllipsisVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";
import { Avatar } from "@fluentui/react-components";

import { AdminReference } from "@/queries/admin/references";

import { referenceStatusList } from "@/redux/features/candidateProfileSlice";
import { useResendReferenceRequestEmail } from "@/mutations/admin/resend-reference-request-email";
import { toast } from "sonner";

const SORTABLE_FIELDS = ["name", "referenceName", "status"] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const ENTRIES_PER_PAGE = 10;

const ReferenceTable = ({
  references,
  setSelectedReference,
}: {
  references?: AdminReference[];
  setSelectedReference: (v: AdminReference | undefined) => void;
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("name");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const sortedEntries: AdminReference[] | undefined = useMemo(() => {
    if (!references) {
      return;
    }

    switch (sortField) {
      case "name":
        return [...references].sort(
          (a, b) =>
            (a.user.name ?? "").localeCompare(b.user.name ?? "") *
            sortDirection,
        );
      case "referenceName":
        return [...references].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
      case "status":
        return [...references].sort(
          (a, b) =>
            (referenceStatusList.indexOf(a.status) -
              referenceStatusList.indexOf(b.status)) *
            sortDirection,
        );
    }
  }, [references, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedEntries) {
      return 1;
    }

    return Math.ceil(sortedEntries.length / ENTRIES_PER_PAGE);
  }, [sortedEntries]);

  const currentPageEntries = useMemo(
    () =>
      sortedEntries?.slice(
        (page - 1) * ENTRIES_PER_PAGE,
        page * ENTRIES_PER_PAGE,
      ),
    [sortedEntries, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("name")}
                  sortDirection={
                    sortField === "name" ? sortDirection : undefined
                  }
                >
                  Name
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("status")}
                  sortDirection={
                    sortField === "status" ? sortDirection : undefined
                  }
                >
                  Status
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("referenceName")}
                  sortDirection={
                    sortField === "referenceName" ? sortDirection : undefined
                  }
                >
                  Reference
                </TableRowItem>
                <TableRowItem heading>Title</TableRowItem>
                <TableRowItem heading>Company</TableRowItem>
                <TableRowItem heading>Relationship</TableRowItem>
                <TableRowItem heading>Email</TableRowItem>
                <TableRowItem heading>Survey Link</TableRowItem>
                <TableRowItem heading button></TableRowItem>
                <TableRowItem heading button></TableRowItem>
              </TableRow>
            </thead>
            <tbody>
              {currentPageEntries?.map((reference) => (
                <AdminRow
                  key={`candidate-${reference.id}`}
                  reference={reference}
                  selectReference={() => setSelectedReference(reference)}
                />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const AdminRow = ({
  reference,
  selectReference,
}: {
  reference: AdminReference;
  selectReference: () => void;
}) => {
  const { resendReferenceRequestEmail, isResendingReferenceRequestEmail } =
    useResendReferenceRequestEmail(reference.id);

  const allowRequest = useMemo(() => {
    return reference.status === "Requested";
  }, [reference.status]);

  return (
    <TableRow>
      <TableRowItem>
        <div className="flex flex-row gap-3 items-center">
          <Avatar
            image={{ src: reference.user.profilePictureLink }}
            name={reference.user.name}
            size={40}
          />
          <span className="text-[#0C0D0D] text-sm font-medium">
            {reference.user.name}
          </span>
        </div>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{reference.status}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {reference.referenceName}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{reference.title}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{reference.company}</span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{reference.relationship}</span>
      </TableRowItem>
      <TableRowItem>
        {Boolean(reference.email) && (
          <div
            className="text-xs text-[#07574b] font-medium hover:underline cursor-pointer"
            onClick={() => {
              void navigator.clipboard.writeText(reference.email ?? "");
              toast.success("Email copied to clipboard");
            }}
          >
            <span className="flex flex-row items-center gap-1">
              Copy
              <ClipboardCopy size={16} />
            </span>
          </div>
        )}
      </TableRowItem>
      <TableRowItem>
        <a
          href={reference.surveyLink}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#646cff] font-medium hover:underline"
        >
          Link
        </a>
      </TableRowItem>
      <TableRowItem>
        {allowRequest && (
          <Button
            variant="secondary"
            size="sm"
            disabled={isResendingReferenceRequestEmail}
            onClick={() => resendReferenceRequestEmail()}
          >
            Resend Request
          </Button>
        )}
      </TableRowItem>
      <TableRowItem>
        <EllipsisVertical
          className="cursor-pointer"
          size={16}
          stroke="#B1B9B8"
          onClick={selectReference}
        />
      </TableRowItem>
    </TableRow>
  );
};

export default ReferenceTable;
